import { useSpaceParameterOpenClause } from '@src/components/AppLayout/SpaceParameters';
import { OrgConfigurationKey, useOrgConfiguration } from '@src/providers/OrgConfigurationProvider';
import {
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  CUSTOMER_TRADE,
  CustomerTrade,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  DEFAULT_MAX_ROWS,
  ExportMenuItems,
  filterByColumnMainMenuItems,
  FormControlSizes,
  IconName,
  MixpanelEvent,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useCustomerTradeColumns,
  useMixpanel,
  usePersistedBlotterTable,
  useWsBlotterTable,
  WSBlotterTableMaxRecordsReachedWarning,
  type Column,
} from '@talos/kyoko';
import type { GetMainMenuItemsParams, RowDoubleClickedEvent } from 'ag-grid-community';
import { useMemo } from 'react';
import { useFeatureFlag } from '../../../hooks';
import {
  colIDToFilterBuilderKey,
  CUSTOMER_TRADE_SPACE_PARAMETER_SUPPORT,
  onlyServerFilterKeys,
  useCustomerTradeFilter,
  type CustomerTradeFilter,
} from './useCustomerTradeFilter';
import { useCustomerTradeMenu } from './useCustomerTradeMenu';

export interface CustomerTradesBlotterTableProps {
  /** Unique id per blotter instance */
  blotterID: string;
  /** ID for common blotter type persistence; usually a subset of the blotterId to represent blotter area 'type' */
  blotterPersistID?: string;
  orderID?: string;
  rfqID?: string;
  tabLabel?: string;
  defaultColumns?: (keyof CustomerTrade | Partial<Column>)[];
  defaultFilter?: CustomerTradeFilter;
  persistFilter?: boolean;
  initialIsOpen?: boolean;
  onCloneTab?: () => void;
  onRowDoubleClicked?: (event: RowDoubleClickedEvent<CustomerTrade>) => void;
}

export function CustomerTradesBlotterTable({
  orderID,
  rfqID,
  blotterID,
  blotterPersistID = blotterID,
  tabLabel,
  defaultColumns,
  defaultFilter,
  persistFilter,
  initialIsOpen,
  onCloneTab,
  onRowDoubleClicked,
}: CustomerTradesBlotterTableProps) {
  const { getConfig } = useOrgConfiguration();
  const mixpanel = useMixpanel();

  const columns = useCustomerTradeColumns({ defaultColumns });

  const persistedBlotterTable = usePersistedBlotterTable(
    blotterPersistID,
    { filter: defaultFilter },
    { persistFilter }
  );

  const filterResults = useCustomerTradeFilter({
    persistedBlotterTable,
  });
  const { clientSideFilter: clientLocalFilter, blotterTableFilterProps, filterBuilderProps } = filterResults;

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps,
  });
  const openClause = useSpaceParameterOpenClause(
    filterBuilderAccordion.openClause,
    CUSTOMER_TRADE_SPACE_PARAMETER_SUPPORT
  );

  const CustomerTradeMenu = useCustomerTradeMenu({
    openClause,
    filterableProperties: filterBuilderProps.properties,
  });

  const getExtraMainMenuItems = useCallbackRef((params: GetMainMenuItemsParams) => {
    return filterByColumnMainMenuItems({
      params,
      colIDToFilterBuilderKey,
      openClause,
      mixpanel,
    });
  });

  const columnsWithMenu = useMemo(
    () => [...columns, ...CustomerTradeMenu.columns],
    [CustomerTradeMenu.columns, columns]
  );

  const { showBlotterPauseButton } = useFeatureFlag();

  const request = useMemo(
    () => ({
      name: CUSTOMER_TRADE,
      tag: blotterID,
      sort_by: '-TransactTime',
      ...(orderID != null && { OrderID: orderID }),
      ...(rfqID != null && { RFQID: rfqID }),
      ...removeEmptyFilters(onlyServerFilterKeys(filterResults.filter)),
    }),
    [blotterID, orderID, rfqID, filterResults.filter]
  );

  // This call owns the true filter state
  const blotterTable = useWsBlotterTable({
    request,
    rowID: CustomerTrade.rowID,
    clientLocalFilter,
    persistence: persistedBlotterTable,
    columns: columnsWithMenu,
    getExtraMainMenuItems,
    startingRowLimit: getConfig(OrgConfigurationKey.BlotterRowsMax, DEFAULT_MAX_ROWS),
    pauseParams: {
      showPauseButton: showBlotterPauseButton,
    },
    contextMenuOptions: { getCustomContextMenuItems: CustomerTradeMenu.getCustomContextMenuItems },
    gridOptions: {
      onRowDoubleClicked,
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
    },
    exportOptions: {
      name: 'CustomerTrades',
      tabLabel: tabLabel,
    },
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  const handleCloneTab = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.CloneTab);
    onCloneTab?.();
    extrasMenuPopover.close();
  });

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTableFilterProps}
        {...blotterTable.blotterTableFiltersProps}
        suffix={
          <BlotterTableExtrasMenu {...extrasMenuPopover}>
            <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
            {onCloneTab && (
              <Button
                startIcon={IconName.Duplicate}
                variant={ButtonVariants.Default}
                size={FormControlSizes.Small}
                onClick={handleCloneTab}
              >
                Clone Tab
              </Button>
            )}
          </BlotterTableExtrasMenu>
        }
      />
      <WSBlotterTableMaxRecordsReachedWarning {...blotterTable.paginationLimit} getTimestamp={r => r.TransactTime} />
      <BlotterTable {...blotterTable} />
      {CustomerTradeMenu.dialogs}
    </>
  );
}
